/**
 * XLM - Innovation & Technology | João Rodrigues | v2.0 | 2026*
 * 
 * Sugestão de Anonimização com NLP:
 * - Implementação de processamento em chunks (máx. 60K caracteres)
 * - Algoritmo de matching de coordenadas/offsets corrigido e otimizado de forma a evitar propagação de erros
 */

import { useEffect, useRef,useState } from "react";
import { AnonimizeStateState } from "../types/AnonimizeState";
import { Entity, normalizeEntityString } from "../types/Entity";
import { UserFile } from "../types/UserFile";
import { Button } from "./BootstrapIcons";
import { useEntities } from "./uses";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getProfile, ProfileI, useAvaiableProfiles, useProfile } from "types/Profile";

import App from "App";


export function SuggestButton({ setRequesting, file, requesting, state }: { setRequesting: (b: boolean) => void, file: UserFile, requesting: boolean, state: AnonimizeStateState }) {
    let ents = useEntities(file.pool)
    const disabled = ents.length > 0 || requesting || state !== AnonimizeStateState.TAGGED;
    const signal = useRef<AbortController | null>(null)

    //Updating the logic in this function to check which profile is in use to cover a special case
    //for anonymization within the main/secondary STJ profiles;

    const [profile] = useProfile();

    useEffect(() => {
        signal.current = new AbortController();
        return () => {
            signal.current?.abort()
        }
    }, [])

    const [progress, setProgress] = useState({ current: 0, total: 0 });

    if (requesting) {
        return <button className="btn btn-small btn-primary m-1 p-1" disabled>
                <span className="spinner-border spinner-border-sm" role="status"></span> 
                    {progress.total > 0 
                ? ` A processar ${progress.current}/${progress.total}...` 
                : " A preparar..."}
                </button>
    }

    return <Button 
            i="file-earmark-play" 
            text="Sugerir Anonimização" 
            className={`btn btn-small btn-primary m-1 p-1 ${disabled ? "border-0 bg-white text-muted" : ""}`} 
            onClick={() => {

                setRequesting(true);
                runRemoteNlp(file, signal.current?.signal,(current,total) => {
                    setProgress({ current, total });
                })
                .then(() => {
                    setRequesting(false);
                    setProgress({ current: 0, total: 0 });
                    file.profile = profile?.name;
                    file.checkCountPES();
                });
        }}

    disabled={disabled} />
}


interface RemoteEntity {
    text: string,
    label_: string,
    start_char: number,
    end_char: number
}


function textFrom(node: Node, sizeMap?: WeakMap<Node, number>): string {
    if (node.nodeType === Node.COMMENT_NODE) {
        if (sizeMap) sizeMap.set(node, 0);
        return "";
    }

    if (node.nodeType === document.TEXT_NODE) {
        const text = (node.textContent || "").replace(/\u00A0/g, " ");
        if (sizeMap) sizeMap.set(node, text.length);
        return text;
    }

    if(node.nodeType === Node.ELEMENT_NODE){
        const el = node as Element;
        const tagName = el.tagName.toUpperCase();

        if (["STYLE", "SCRIPT", "HEAD", "META", "LINK"].includes(tagName)) {
            if (sizeMap) sizeMap.set(node, 0);
            return "";
        }
        const text = textFrom_while(el.childNodes, sizeMap);
        
        if(tagName === "BR"){
            if (sizeMap) sizeMap.set(node, 1);
            return "\n";
        }
        
        const includesBlock = ["P","DIV","H1","H2","H3","TR","LI","FOOTER"].includes(tagName);
        if (includesBlock) {
            const cleanText = text.replace(/\n+$/, ""); 
            const result = cleanText + "\n";
            if (sizeMap) sizeMap.set(node, result.length);
            return result;
        }
        if (sizeMap) sizeMap.set(node, text.length);
        return text;
    }
    
    if (sizeMap) sizeMap.set(node, 0);
    return "" ;
}

function textFrom_while(childNodes: NodeList, sizeMap?: WeakMap<Node, number>): string {
    let result = "";
    const nodes = Array.from(childNodes);
    let i = 0;

    while (i < nodes.length) {
        result += textFrom(nodes[i], sizeMap);
        i++;
    }

    return result;
}


function fn_createChuncks(doc:Element, charLimit: number = 40000,sizeMap?: WeakMap<Node, number>) : string[]{
    const chunks :string[] = [];
    let currentChunkText = "";
    const nodes = Array.from(doc.childNodes);

    for(const node of nodes){
        const nodeText = textFrom(node,sizeMap);
      

        if(nodeText.length > charLimit){

            if(currentChunkText.length > 0){
                chunks.push(currentChunkText);
                currentChunkText="";
            }
        

            let remainingText = nodeText;
            while(remainingText.length > charLimit){
                let cutIndex = remainingText.lastIndexOf(" ", charLimit);
                if(cutIndex === -1){
                    cutIndex = charLimit;
                }
                
                chunks.push(remainingText.substring(0,cutIndex));
                remainingText = remainingText.substring(cutIndex);
            }
            currentChunkText = remainingText;
            continue;
        }

        if((currentChunkText.length + nodeText.length) > charLimit){
            chunks.push(currentChunkText);
            currentChunkText ="";
        }
        currentChunkText +=nodeText;
       
    }
    
    if(currentChunkText.length > 0){
        chunks.push(currentChunkText);
    }
    return chunks;
}

function fn_adjusRes(res: RemoteEntity[], currentOffset:number){
    return res.map((ent:RemoteEntity) => ({
        ...ent,
        start_char: ent.start_char + currentOffset,
        end_char: ent.end_char + currentOffset
    }));
}

async function calculateBatchSize():Promise<number>{
    try{
        const res = await fetch ("/api/batch_size");
        const data = await res.json();
        return data.batchSize;
    }
    catch{
        return 40000;
    }

}



let runRemoteNlpRequesting = false;
export async function runRemoteNlp(file: UserFile, abort?: AbortSignal, onProgress?: (current: number,total: number) => void ){
    const startTime = performance.now();

    if (runRemoteNlpRequesting) return;
    runRemoteNlpRequesting = true;
    try{
        let doc = file.doc;
        let pool = file.pool;
        const sizeMap = new WeakMap<Node, number>();
        // Calcular Batch Size dinamicamente
        const totalChars = doc.textContent?.length || 0;
        const dynamicBatchSize = await calculateBatchSize();
        const textChunks: string [] = fn_createChuncks(doc,dynamicBatchSize,sizeMap);
        if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
            console.info(`Documento: ${totalChars} chars → BATCH_SIZE: ${dynamicBatchSize} → ${textChunks.length} batches`);
        }
        const allEntities : RemoteEntity [] = await fetchChuncks(textChunks,abort,onProgress);

        if (allEntities.length === 0) {
            runRemoteNlpRequesting = false;
            return;
        }
        const unifiedText = textChunks.join("");
        pool.originalText = unifiedText;
    
        const {entities,errors} = matchEntities(allEntities,unifiedText);

        
        const totalDetected = allEntities.length ;
        const totalErrors = errors.length;
        const successRate = (((totalDetected - totalErrors) / totalDetected) * 100).toFixed(2);

        
        if (errors.length > 0) {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.log("NLP: " + errors.length + " entidades não localizadas (" + successRate + "% sucesso)");
                console.log("Entidades falhadas: " + errors.map(e => e.text).join(", "));
            }
       
        }

        const entitiesArray = Object.values(entities);
        pool.entities = entitiesArray.filter(e => e.offsets.length > 0).sort((a, b) => a.offsets[0].start - b.offsets[0].start);
        pool.nodeSizes = sizeMap;  
        pool.updateOrder("Sugerir");
        runRemoteNlpRequesting = false;
        const endTime = performance.now();
        if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
            console.log(`TEMPO TOTAL: ${((endTime - startTime) / 1000).toFixed(1)}s`);
        }
        return ;
    } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.info('Anonimização cancelada pelo utilizador');
            }
        } else {
            if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
                console.error('Erro na anonimização:', err);
            }
        }
    } finally {
        runRemoteNlpRequesting = false;
    }
}

function matchEntities(allEntities:RemoteEntity[],unifiedText:string){
    let entities: { [key: string]: Entity } = {};
    let usedIndexes: { [key: number]: boolean } = {};
    let errors: RemoteEntity[] = []
    let exactCount = 0;
    let proximityCount = 0;

    //let count = 0;
    const DELTA = 100;
    const unifiedTextLength = unifiedText.length;
    
    for (let ent of allEntities as RemoteEntity[]) {
     
        let normalizeText = normalizeEntityString(ent.text);
        let id = `${normalizeText}_${ent.label_}`;

        if (!(id in entities)) {
            entities[id] = new Entity(ent.label_);
        }

        const exactMatch = unifiedText.substring(ent.start_char, ent.end_char);
        
        if (exactMatch === ent.text) {
            exactCount++;
            entities[id].addOffset([{ 
                start: ent.start_char, 
                end: ent.end_char - 1, 
                preview: ent.text
            }]);
            continue;
        }
        proximityCount++;
        const windowStart = Math.max(0,ent.start_char -DELTA); 
        const windowEnd = Math.min(unifiedTextLength,ent.end_char + DELTA);


        const textWindow = unifiedText.substring(windowStart,windowEnd);
        const regex =new RegExp(ent.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), "g");
        const matches= Array.from(textWindow.matchAll(regex));

        let minDist = Infinity
        let minIndex = -1
        for (const m of matches) {
            const relativeIndex = m.index;
            const absoluteIndex = (relativeIndex?? 0) + windowStart;
            
            const diff = Math.abs(absoluteIndex - ent.start_char);

            if (diff < minDist && !usedIndexes[absoluteIndex]) {
                minDist = diff;
                minIndex = absoluteIndex;
            }
        }

        if (minIndex > -1 && minDist < 2*DELTA) {
            usedIndexes[minIndex] = true;
            
            entities[id].addOffset([{ 
                start: minIndex, 
                end: minIndex + ent.text.length - 1, 
                preview: ent.text.trim()
            }]);
        } else {
           
            errors.push(ent);
        }

       
    }
    if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'qa') {
        console.info(`exactMatch: ${exactCount}, proximity: ${proximityCount}`);
    }
    return {entities,errors}
}


async function fetchChuncks(textChunks: string[],abort?: AbortSignal,onProgress?: (current: number,total: number) => void ){
    const total = textChunks.length;
    let completed = 0;
    const allEntities = [];

    for (let i = 0; i < total; i++) {
        const text = textChunks[i];
        const offset = textChunks.slice(0, i).reduce((sum, t) => sum + t.length, 0);
        
        const chunkBlob = new Blob([text], { type: 'text/plain' });
        const fd = new FormData();
        fd.append("file", chunkBlob, "input.txt");

        const res = await fetch(window.location.origin + "/from-text", {
            method: "POST",
            body: fd,
            signal: abort
        }).then(r => r.ok ? r.json() : []);

        allEntities.push(...fn_adjusRes(res, offset));
        completed++;
        if (onProgress) onProgress(completed, total);
    }

    return allEntities;
}

